// Tab "Khách chờ thanh toán" của màn bán hàng: lịch hẹn S7 – Xác nhận làm (API POS V2 – waiting-orders)
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { CalendarClock, Hourglass, Loader2, RefreshCw, ReceiptText, Search, Stethoscope, UserRound, WifiOff, X } from 'lucide-react';
import { Select } from 'antd';
import { TYPE_LABEL, type ItemType, type WaitingAppointment } from '../data';
import { formatPhone, money, viFilterOption } from '../utils';

type TypeFilter = 'all' | ItemType;
const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'Tất cả' }, { key: 'service', label: 'Dịch vụ' }, { key: 'product', label: 'Sản phẩm' }, { key: 'package', label: 'Gói' },
];

interface Props {
  items: WaitingAppointment[];
  loading: boolean;
  error: string;
  lastLoaded: Date | null;
  onReload: () => void;
  activeId: string | null;
  /** Lịch hẹn đang nạp giỏ hàng */
  pickingId: string | null;
  onPick: (w: WaitingAppointment) => void;
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

export default function WaitingPanel({ items, loading, error, lastLoaded, onReload, activeId, pickingId, onPick }: Props) {
  const [query, setQuery] = useState('');
  const [branch, setBranch] = useState('all');
  const [type, setType] = useState<TypeFilter>('all');

  const branches = useMemo(() => Array.from(new Set(items.map(i => i.branchName).filter(Boolean))).sort(), [items]);
  // Lọc theo từ khóa + cơ sở trước; số đếm trên thẻ loại tính trên tập này
  const base = useMemo(() => {
    const q = normalize(query.trim());
    const digits = query.replace(/\D/g, '');
    return items.filter(w =>
      (branch === 'all' || w.branchName === branch) &&
      (!q || normalize(w.customerName).includes(q) || w.code.toLowerCase().includes(q) || normalize(w.primaryService).includes(q) ||
        (digits.length >= 3 && w.customerPhone.includes(digits))));
  }, [items, query, branch]);
  const rows = useMemo(() => (type === 'all' ? base : base.filter(w => w.itemTypes?.includes(type))), [base, type]);
  // API waiting-orders chưa trả loại hàng → thẻ Dịch vụ / Sản phẩm / Gói chưa lọc được
  const hasTypeData = items.some(w => w.itemTypes);
  const countOf = (k: TypeFilter) => (k === 'all' ? base.length : base.filter(w => w.itemTypes?.includes(k)).length);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-2 border-b border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center"><Search className="h-4 w-4 text-slate-400" /></span>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Tìm tên, SĐT, mã lịch hẹn, dịch vụ…"
              className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-9 text-sm outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20" />
            {query && (
              <button onClick={() => setQuery('')} className="absolute inset-y-0 right-1 flex w-8 items-center justify-center text-slate-400 hover:text-slate-600" aria-label="Xóa từ khóa">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {branches.length > 1 && (
            <Select size="large" value={branch} onChange={setBranch} showSearch filterOption={viFilterOption}
              popupMatchSelectWidth={false} className="w-44" notFoundContent="Không có cơ sở"
              options={[{ value: 'all', label: 'Tất cả cơ sở' }, ...branches.map(b => ({ value: b, label: b }))]} />
          )}
          <button onClick={onReload} disabled={loading} title={loading ? 'Đang tải…' : 'Tải lại danh sách'} className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-wait">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loading ? 'Đang tải…' : lastLoaded ? dayjs(lastLoaded).format('HH:mm') : 'Tải lại'}</span>
          </button>
        </div>
        <div role="tablist" aria-label="Lọc theo loại hàng" className="flex max-w-full overflow-x-auto rounded-lg bg-slate-100 p-1">
          {TYPE_FILTERS.map(t => {
            const active = type === t.key;
            const pending = t.key !== 'all' && !hasTypeData;
            return (
              <button key={t.key} role="tab" aria-selected={active} onClick={() => setType(t.key)}
                title={pending ? 'Hệ thống chưa trả loại hàng của lịch hẹn – chưa lọc được' : undefined}
                className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-4 text-sm font-semibold transition ${active ? 'bg-white text-[#2c6b7f] shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
                {t.label}
                {!pending && (
                  <span className={`rounded-full px-1.5 text-xs ${active ? 'bg-[#ecf6f9] text-[#2c6b7f]' : 'bg-slate-200 text-slate-500'}`}>{countOf(t.key)}</span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500">
          Khách đã được tư vấn chuyển sang <b>S7 – Xác nhận làm</b> và chưa tạo đơn. Tự cập nhật mỗi 30 giây.
          {items.length > 0 && <> · <b>{rows.length}</b>/{items.length} khách</>}
        </p>
      </div>

      <div className="pos-scroll min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
        {error && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <span className="flex items-center gap-1.5"><WifiOff className="h-4 w-4 shrink-0" /> Không tải được danh sách chờ: {error}</span>
            <button onClick={onReload} className="shrink-0 font-semibold underline">Thử lại</button>
          </div>
        )}
        {loading && !items.length ? (
          <div className="grid gap-3 xl:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-36 animate-pulse rounded-xl bg-white" />)}
          </div>
        ) : rows.length === 0 && type !== 'all' && base.length > 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-slate-500">
            <ReceiptText className="h-10 w-10 text-slate-300" />
            <p>{hasTypeData ? `Không có khách chờ thanh toán ${TYPE_LABEL[type].toLowerCase()}` : 'Chưa lọc được theo loại hàng'}</p>
            {!hasTypeData && <p className="max-w-sm text-xs text-slate-400">Hệ thống chưa trả loại hàng (dịch vụ / sản phẩm / gói) của lịch hẹn.</p>}
            <button onClick={() => setType('all')} className="mt-1 h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">Xem tất cả</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-slate-500">
            <ReceiptText className="h-10 w-10 text-slate-300" />
            <p>{query || branch !== 'all' ? 'Không có khách phù hợp' : 'Chưa có khách nào chờ thanh toán'}</p>
            <p className="max-w-sm text-xs text-slate-400">Khi tư vấn chuyển lịch hẹn sang “Xác nhận làm”, khách sẽ hiện ở đây.</p>
          </div>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {rows.map(w => {
              const isActive = w.id === activeId;
              const isPicking = w.id === pickingId;
              return (
                <div key={w.id} className={`flex flex-col rounded-xl border bg-white p-4 transition ${isActive ? 'border-[#3e91ab] ring-2 ring-[#3e91ab]/20' : 'border-slate-200 hover:border-[#3e91ab]/60 hover:shadow-sm'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <UserRound className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="truncate text-base font-bold text-slate-800">{w.customerName || 'Khách lẻ'}</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                        {w.customerPhone && <span>{formatPhone(w.customerPhone)}</span>}
                        <span className="font-mono">{w.code}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-lg font-bold text-[#2c6b7f]">{money(w.total)}đ</div>
                      <span className="inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">{w.tagName}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    {w.consultant && <span className="flex items-center gap-1"><Stethoscope className="h-3 w-3" />{w.consultant}</span>}
                    {w.scheduledAt && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />Hẹn {dayjs(w.scheduledAt).format('HH:mm DD/MM/YYYY')}</span>}
                    {w.branchName && <span>{w.branchName}</span>}
                    {w.itemTypes?.map(t => <span key={t} className="rounded bg-slate-100 px-1.5 font-medium text-slate-500">{TYPE_LABEL[t]}</span>)}
                  </div>
                  <div className="mt-2 flex-1 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    {w.primaryService ? (
                      <span className="text-slate-700">
                        {w.primaryService}
                        {w.itemsCount > 1 && <span className="ml-1 text-xs text-slate-500">+{w.itemsCount - 1} dịch vụ khác</span>}
                      </span>
                    ) : <span className="text-slate-400">Lịch hẹn chưa có dịch vụ</span>}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-xs text-slate-400" title="Lúc chuyển sang Xác nhận làm">
                      {w.waitingSince && <><Hourglass className="h-3 w-3" />Chờ từ {dayjs(w.waitingSince).format('HH:mm DD/MM')}</>}
                    </span>
                    <button onClick={() => onPick(w)} disabled={isActive || isPicking || !!pickingId || w.itemsCount <= 0}
                      className="flex h-11 items-center gap-1.5 rounded-lg bg-[#357f96] px-5 text-sm font-bold text-white hover:bg-[#2c6b7f] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
                      {isPicking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
                      {isActive ? 'Đang thanh toán' : isPicking ? 'Đang nạp…' : 'Thu tiền'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
